import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { splitText } from 'griffo'

interface KeyframeData {
	element: HTMLElement
	chars: HTMLSpanElement[]
	body: HTMLElement | null
	reverts: (() => void)[]
	start: number
	end: number
	fadeIn: number
	fadeOut: number
	// 0 | 1 = idle at that opacity (no need to re-write styles); null = mid-animation
	charsIdle: 0 | 1 | null
	bodyIdle: 0 | 1 | null
}

export class ScienceAnimation extends UcoastEl {
	static htmlSelector = 'science-animation'

	private outer: HTMLElement
	private sticky: HTMLElement
	private canvas?: HTMLCanvasElement
	private ctx?: CanvasRenderingContext2D
	private poster?: HTMLImageElement
	private loader?: HTMLElement
	private skipButton?: HTMLButtonElement
	private keyframeData: KeyframeData[] = []
	private reducedMotion: boolean

	private frameUrls: string[] = []
	private frames: (HTMLImageElement | null)[] = []
	private framesLoaded: boolean[] = []
	private loadedCount = 0
	private firstFrameReady = false
	private currentFrameIndex = -1
	private dpr = 1
	private intersected = false
	private loaderShown = false
	private loaderTimeoutId?: number
	private lastScrollY = 0
	private scrollDirection: 'up' | 'down' = 'down'

	private boundScrollHandler?: () => void
	private boundResizeHandler?: () => void
	private intersectionObserver?: IntersectionObserver
	private frameRequestId?: number
	private targetProgress = 0
	private smoothedProgress = 0

	private readonly smoothingFactor = 0.2
	private readonly smoothingEpsilon = 0.0005
	private readonly charStaggerTotal = 0.4
	private readonly loaderShowDelayMs = 250
	private readonly loadConcurrency = 6
	private readonly bodyDelay = 0.01
	private readonly bodyFadeDuration = 0.02

	constructor() {
		super()
		this.outer = q.rs('.science-animation__outer', this)
		this.sticky = q.rs('.science-animation__sticky', this)
		this.canvas = q.os<HTMLCanvasElement>('[data-canvas]', this) ?? undefined
		this.poster = q.os<HTMLImageElement>('.science-animation__poster', this) ?? undefined
		this.loader = q.os<HTMLElement>('[data-loader]', this) ?? undefined
		this.skipButton = q.os<HTMLButtonElement>('[data-skip]', this) ?? undefined
		this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

		this.setupKeyframeText()
		this.parseFrameUrls()
		this.setupCanvas()
		this.setupSkipButton()

		if (this.reducedMotion) {
			this.applyReducedMotionLayout()
			return
		}

		this.boundScrollHandler = this.onScroll.bind(this)
		this.boundResizeHandler = this.onResize.bind(this)

		window.addEventListener('scroll', this.boundScrollHandler, { passive: true })
		window.addEventListener('resize', this.boundResizeHandler)

		this.observeForPreload()
		this.onScroll()
	}

	private setupKeyframeText() {
		const keyframeEls = Array.from(
			this.querySelectorAll<HTMLElement>('[data-keyframe]')
		)
		for (const kf of keyframeEls) {
			const chars: HTMLSpanElement[] = []
			const reverts: (() => void)[] = []

			// Caption + title are split per-char for the staggered opacity fade.
			// The body copy (split-right) stays as a single block. Blur was
			// dropped from both — CSS filters force a new compositor layer per
			// element that's re-rasterized on every change, which dominated the
			// scroll cost.
			const charTargets = kf.querySelectorAll<HTMLElement>(
				'.science-animation__caption, .science-animation__title'
			)
			for (const el of charTargets) {
				const split = splitText(el, { type: 'chars' })
				for (const char of split.chars) {
					char.style.willChange = 'opacity'
					char.style.opacity = '0'
					chars.push(char)
				}
				reverts.push(split.revert)
			}

			const body = kf.querySelector<HTMLElement>(
				'.science-animation__split-right'
			)
			if (body) {
				body.style.willChange = 'opacity'
				body.style.opacity = '0'
			}

			kf.style.opacity = '1'

			this.keyframeData.push({
				element: kf,
				chars,
				body,
				reverts,
				start: parseFloat(kf.dataset.start ?? '0') / 100,
				end: parseFloat(kf.dataset.end ?? '100') / 100,
				fadeIn: parseFloat(kf.dataset.fadeIn ?? '5') / 100,
				fadeOut: parseFloat(kf.dataset.fadeOut ?? '5') / 100,
				charsIdle: 0,
				bodyIdle: 0,
			})
		}
	}

	private parseFrameUrls() {
		const breakpoint = parseInt(this.dataset.mobileBreakpoint ?? '749', 10)
		const isMobile = window.matchMedia(`(max-width: ${breakpoint}px)`).matches

		const mobileScript = this.querySelector<HTMLScriptElement>(
			'script[data-science-frames-mobile]'
		)
		const desktopScript = this.querySelector<HTMLScriptElement>(
			'script[data-science-frames]'
		)
		const scriptEl = isMobile && mobileScript ? mobileScript : desktopScript
		if (!scriptEl) return
		try {
			const urls = JSON.parse(scriptEl.textContent ?? '[]')
			if (Array.isArray(urls)) {
				this.frameUrls = urls.filter((u): u is string => typeof u === 'string')
				this.frames = new Array(this.frameUrls.length).fill(null)
				this.framesLoaded = new Array(this.frameUrls.length).fill(false)
			}
		} catch {
			/* malformed JSON — leave frames empty, poster will remain visible */
		}
	}

	private setupCanvas() {
		if (!this.canvas) return
		const ctx = this.canvas.getContext('2d', { alpha: false })
		if (!ctx) {
			this.canvas = undefined
			return
		}
		this.ctx = ctx
		this.resizeCanvas()
	}

	private resizeCanvas() {
		if (!this.canvas) return
		this.dpr = Math.min(window.devicePixelRatio || 1, 2)
		const { clientWidth, clientHeight } = this.sticky
		const targetW = Math.round(clientWidth * this.dpr)
		const targetH = Math.round(clientHeight * this.dpr)
		if (this.canvas.width !== targetW || this.canvas.height !== targetH) {
			this.canvas.width = targetW
			this.canvas.height = targetH
		}
		if (this.firstFrameReady && this.currentFrameIndex >= 0) {
			this.drawFrame(this.currentFrameIndex, true)
		}
	}

	private setupSkipButton() {
		if (!this.skipButton) return
		this.skipButton.addEventListener('click', () => this.skip())
	}

	private skip() {
		// Direction-aware skip: scrolling down -> jump past the section;
		// scrolling up -> jump back to before it. Either way, the animation
		// itself keeps playing during the scroll; we never collapse the section.
		const outerTop = this.outer.getBoundingClientRect().top + window.scrollY
		const outerBottom = outerTop + this.outer.offsetHeight - window.innerHeight
		const target = this.scrollDirection === 'up' ? outerTop : outerBottom
		window.scrollTo({ top: target, behavior: 'smooth' })

		// Move focus to a relevant landmark so keyboard / screen reader users
		// land somewhere sensible after the smooth scroll.
		const focusTarget =
			this.scrollDirection === 'up'
				? (this.previousElementSibling as HTMLElement | null)
				: (this.nextElementSibling as HTMLElement | null)
		if (focusTarget) {
			const focusable = focusTarget.querySelector<HTMLElement>(
				'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
			)
			focusable?.focus({ preventScroll: true })
		}
	}

	private observeForPreload() {
		// Start loading frames when section is within 1.5 viewports of entering.
		// This lets the load happen while the user is still scrolling toward it,
		// so by the time they arrive the spinner rarely needs to show.
		if (!('IntersectionObserver' in window)) {
			this.startFrameLoad()
			return
		}
		this.intersectionObserver = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting && !this.intersected) {
						this.intersected = true
						this.startFrameLoad()
						this.intersectionObserver?.disconnect()
					}
				}
			},
			{ rootMargin: '150% 0% 150% 0%' }
		)
		this.intersectionObserver.observe(this)
	}

	private startFrameLoad() {
		if (this.frameUrls.length === 0) return

		// Anchor frames first (0, last, then quarters) so coarse scrubbing works
		// almost immediately even before every frame is decoded.
		const total = this.frameUrls.length
		const anchorIndices = new Set<number>([
			0,
			total - 1,
			Math.floor(total / 2),
			Math.floor(total / 4),
			Math.floor((total * 3) / 4),
		])
		const queue: number[] = [
			...Array.from(anchorIndices),
			...Array.from({ length: total }, (_, i) => i).filter(
				(i) => !anchorIndices.has(i)
			),
		]

		let inflight = 0
		const pumpNext = () => {
			while (inflight < this.loadConcurrency && queue.length > 0) {
				const idx = queue.shift() as number
				if (this.framesLoaded[idx]) continue
				inflight++
				this.loadFrame(idx).finally(() => {
					inflight--
					pumpNext()
				})
			}
		}
		pumpNext()
	}

	private loadFrame(index: number): Promise<void> {
		return new Promise((resolve) => {
			const img = new Image()
			img.decoding = 'async'
			img.onload = () => {
				this.frames[index] = img
				this.framesLoaded[index] = true
				this.loadedCount++
				this.onFrameLoaded(index)
				resolve()
			}
			img.onerror = () => {
				this.framesLoaded[index] = true
				this.loadedCount++
				resolve()
			}
			img.src = this.frameUrls[index]
		})
	}

	private onFrameLoaded(index: number) {
		if (!this.firstFrameReady) {
			this.firstFrameReady = true
			this.hideLoader()
			if (this.canvas) {
				this.canvas.hidden = false
				if (this.poster) {
					this.poster.classList.add('science-animation__poster--hidden')
				}
			}
			this.requestRender()
			return
		}
		// If the just-loaded frame matches the current scrub target, draw it now.
		const want = this.frameIndexForProgress(this.smoothedProgress)
		if (index === want) {
			this.drawFrame(index)
		}
	}

	private frameIndexForProgress(progress: number): number {
		if (this.frameUrls.length === 0) return -1
		const clamped = Math.max(0, Math.min(1, progress))
		const idx = Math.round(clamped * (this.frameUrls.length - 1))
		return Math.max(0, Math.min(this.frameUrls.length - 1, idx))
	}

	private getProgress(): number {
		const rect = this.outer.getBoundingClientRect()
		const outerHeight = this.outer.offsetHeight
		const viewportHeight = window.innerHeight
		const scrollRange = outerHeight - viewportHeight

		if (scrollRange <= 0) return 0

		const progress = -rect.top / scrollRange
		return Math.max(0, Math.min(1, progress))
	}

	private onScroll() {
		if (this.reducedMotion) return
		const currentY = window.scrollY
		if (currentY > this.lastScrollY) this.scrollDirection = 'down'
		else if (currentY < this.lastScrollY) this.scrollDirection = 'up'
		this.lastScrollY = currentY
		this.targetProgress = this.getProgress()
		this.maybeShowLoader()
		this.requestRender()
	}

	private onResize() {
		this.resizeCanvas()
		this.onScroll()
	}

	private maybeShowLoader() {
		if (this.firstFrameReady || this.loaderShown) return
		if (!this.loader) return
		// Only show the spinner if the user is actually inside the section.
		const rect = this.outer.getBoundingClientRect()
		const inSection = rect.top <= 0 && rect.bottom > window.innerHeight
		if (!inSection) return
		if (this.loaderTimeoutId) return
		this.loaderTimeoutId = window.setTimeout(() => {
			if (this.firstFrameReady) return
			this.loaderShown = true
			if (this.loader) this.loader.hidden = false
		}, this.loaderShowDelayMs)
	}

	private hideLoader() {
		if (this.loaderTimeoutId) {
			window.clearTimeout(this.loaderTimeoutId)
			this.loaderTimeoutId = undefined
		}
		if (this.loader) this.loader.hidden = true
		this.loaderShown = false
	}

	private requestRender() {
		if (this.frameRequestId) return
		this.frameRequestId = window.requestAnimationFrame(() => this.tick())
	}

	private tick() {
		this.frameRequestId = undefined
		const delta = this.targetProgress - this.smoothedProgress

		if (Math.abs(delta) > this.smoothingEpsilon) {
			this.smoothedProgress += delta * this.smoothingFactor
		} else {
			this.smoothedProgress = this.targetProgress
		}

		this.render(this.smoothedProgress)

		if (Math.abs(this.targetProgress - this.smoothedProgress) > this.smoothingEpsilon) {
			this.requestRender()
		}
	}

	private render(progress: number) {
		this.updateCanvas(progress)
		this.updateKeyframes(progress)
	}

	private updateCanvas(progress: number) {
		if (!this.firstFrameReady) return
		const want = this.frameIndexForProgress(progress)
		if (want === -1 || want === this.currentFrameIndex) return
		// If the exact target frame isn't loaded yet, fall back to the nearest loaded
		// frame so we never blank out — works because anchor frames load first.
		const drawn = this.nearestLoadedIndex(want)
		if (drawn === -1) return
		this.drawFrame(drawn)
	}

	private nearestLoadedIndex(target: number): number {
		if (this.framesLoaded[target]) return target
		const max = this.framesLoaded.length - 1
		for (let offset = 1; offset <= max; offset++) {
			const lo = target - offset
			const hi = target + offset
			if (lo >= 0 && this.framesLoaded[lo]) return lo
			if (hi <= max && this.framesLoaded[hi]) return hi
		}
		return -1
	}

	private drawFrame(index: number, force = false) {
		if (!this.ctx || !this.canvas) return
		if (!force && index === this.currentFrameIndex) return
		const img = this.frames[index]
		if (!img) return

		const cw = this.canvas.width
		const ch = this.canvas.height
		// object-fit: cover behavior
		const imgRatio = img.naturalWidth / img.naturalHeight
		const canvasRatio = cw / ch
		let drawW: number, drawH: number, dx: number, dy: number
		if (imgRatio > canvasRatio) {
			drawH = ch
			drawW = ch * imgRatio
			dx = (cw - drawW) / 2
			dy = 0
		} else {
			drawW = cw
			drawH = cw / imgRatio
			dx = 0
			dy = (ch - drawH) / 2
		}
		this.ctx.drawImage(img, dx, dy, drawW, drawH)
		this.currentFrameIndex = index
	}

	private updateKeyframes(progress: number) {
		for (const data of this.keyframeData) {
			const { element, chars, body, start, end, fadeIn, fadeOut } = data
			const fadeInDistance = Math.max(fadeIn, Number.EPSILON)
			const fadeOutDistance = Math.max(fadeOut, Number.EPSILON)
			const fadeInEnd = start + fadeInDistance
			const fadeOutStart = end - fadeOutDistance

			if (progress < start || progress > end) {
				if (data.charsIdle !== 0) {
					this.setCharsState(chars, 0)
					data.charsIdle = 0
				}
				element.style.pointerEvents = 'none'
			} else if (progress < fadeInEnd) {
				const fadeProgress = (progress - start) / fadeInDistance
				this.animateChars(chars, fadeProgress)
				data.charsIdle = null
				element.style.pointerEvents = 'auto'
			} else if (fadeOutStart < end && progress > fadeOutStart) {
				const fadeProgress = (end - progress) / fadeOutDistance
				this.animateChars(chars, fadeProgress)
				data.charsIdle = null
				element.style.pointerEvents = 'auto'
			} else {
				if (data.charsIdle !== 1) {
					this.setCharsState(chars, 1)
					data.charsIdle = 1
				}
				element.style.pointerEvents = 'auto'
			}

			if (body) {
				// Body fades in after the title is fully in, fades out before the
				// title starts leaving. Window is inset within the title's visible
				// range by `bodyDelay` on each side.
				const bodyFadeInStart = fadeInEnd + this.bodyDelay
				const bodyFadeInEnd = bodyFadeInStart + this.bodyFadeDuration
				const bodyFadeOutEnd = fadeOutStart - this.bodyDelay
				const bodyFadeOutStart = bodyFadeOutEnd - this.bodyFadeDuration

				if (
					bodyFadeInStart >= bodyFadeOutEnd ||
					progress <= bodyFadeInStart ||
					progress >= bodyFadeOutEnd
				) {
					if (data.bodyIdle !== 0) {
						this.setBodyState(body, 0)
						data.bodyIdle = 0
					}
				} else if (progress < bodyFadeInEnd) {
					this.setBodyState(
						body,
						(progress - bodyFadeInStart) / this.bodyFadeDuration
					)
					data.bodyIdle = null
				} else if (progress < bodyFadeOutStart) {
					if (data.bodyIdle !== 1) {
						this.setBodyState(body, 1)
						data.bodyIdle = 1
					}
				} else {
					this.setBodyState(
						body,
						(bodyFadeOutEnd - progress) / this.bodyFadeDuration
					)
					data.bodyIdle = null
				}
			}
		}
	}

	private animateChars(chars: HTMLSpanElement[], fadeProgress: number) {
		const charCount = chars.length
		if (charCount === 0) return

		for (let i = 0; i < charCount; i++) {
			const charNorm = charCount > 1 ? i / (charCount - 1) : 0
			const charStart = charNorm * this.charStaggerTotal
			const charEnd = charStart + (1 - this.charStaggerTotal)
			const charProgress = Math.max(
				0,
				Math.min(1, (fadeProgress - charStart) / (charEnd - charStart))
			)
			chars[i].style.opacity = `${charProgress}`
		}
	}

	private setCharsState(chars: HTMLSpanElement[], opacity: number) {
		const value = `${opacity}`
		for (const char of chars) char.style.opacity = value
	}

	private setBodyState(body: HTMLElement, opacity: number) {
		body.style.opacity = `${opacity}`
	}

	private applyReducedMotionLayout() {
		// Reveal all keyframe text and remove the scroll-jack — used by both
		// prefers-reduced-motion users and the Skip button.
		this.classList.add('science-animation--static')
		this.hideLoader()
		if (this.canvas) this.canvas.hidden = true
		if (this.poster) {
			this.poster.classList.remove('science-animation__poster--hidden')
		}
		for (const data of this.keyframeData) {
			data.element.style.opacity = '1'
			for (const char of data.chars) char.style.opacity = '1'
			data.charsIdle = 1
			if (data.body) {
				data.body.style.opacity = '1'
				data.bodyIdle = 1
			}
		}
		if (this.skipButton) this.skipButton.hidden = true
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		if (this.boundScrollHandler) {
			window.removeEventListener('scroll', this.boundScrollHandler)
		}
		if (this.boundResizeHandler) {
			window.removeEventListener('resize', this.boundResizeHandler)
		}
		if (this.frameRequestId) {
			window.cancelAnimationFrame(this.frameRequestId)
			this.frameRequestId = undefined
		}
		if (this.loaderTimeoutId) {
			window.clearTimeout(this.loaderTimeoutId)
			this.loaderTimeoutId = undefined
		}
		this.intersectionObserver?.disconnect()
	}
}
